// ABOUTME: The reason pick-list and optional note behind "Ich kann nicht" (a move) and "Absagen" (a cancel); pro-core
// ABOUTME: writes both onto the Interview Timeline.
import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import DialogContent from '@mui/material/DialogContent';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { AlertDialog } from 'components/dialog';
import { Text } from 'components/Text';
import { getTrans, TranslationKey } from 'modules/i18n';
import { NOTE_MAX_LENGTH } from '../constants';
import { ChangeInterviewRequest, InterviewChangeReason } from '../types';

export type InterviewReasonDialogKind = 'move' | 'cancel';

/**
 * The reasons a talent picks from. InterviewChangeReason is shared with the host and the system: POSITION_FILLED is the
 * host's, NO_TIME_FOUND and CANDIDATE_LEFT_STEP the system's, so none of them is ever offered here.
 */
const REASONS: Record<InterviewReasonDialogKind, InterviewChangeReason[]> = {
  move: [
    InterviewChangeReason.SCHEDULE_CONFLICT,
    InterviewChangeReason.ILLNESS,
    InterviewChangeReason.OTHER
  ],
  cancel: [
    InterviewChangeReason.SCHEDULE_CONFLICT,
    InterviewChangeReason.ILLNESS,
    InterviewChangeReason.FOUND_OTHER_JOB,
    InterviewChangeReason.NO_LONGER_INTERESTED,
    InterviewChangeReason.OTHER
  ]
};

const REASON_COPY: Record<string, TranslationKey> = {
  SCHEDULE_CONFLICT: 'interviews.reason.SCHEDULE_CONFLICT',
  ILLNESS: 'interviews.reason.ILLNESS',
  FOUND_OTHER_JOB: 'interviews.reason.FOUND_OTHER_JOB',
  NO_LONGER_INTERESTED: 'interviews.reason.NO_LONGER_INTERESTED',
  OTHER: 'interviews.reason.OTHER'
};

const COPY: Record<
  InterviewReasonDialogKind,
  { title: TranslationKey; body: TranslationKey; confirm: TranslationKey }
> = {
  move: {
    title: 'interviews.move.title',
    body: 'interviews.move.body',
    confirm: 'interviews.move.submit'
  },
  cancel: {
    title: 'interviews.cancel.title',
    body: 'interviews.cancel.body',
    confirm: 'interviews.cancel.submit'
  }
};

interface Props {
  kind: InterviewReasonDialogKind | null;
  isSubmitting: boolean;
  errorMessage?: string;
  onClose: () => void;
  onConfirm: (body: ChangeInterviewRequest) => void;
}

export const InterviewReasonDialog = ({
  kind,
  isSubmitting,
  errorMessage,
  onClose,
  onConfirm
}: Props) => {
  const [reason, setReason] = useState<InterviewChangeReason | ''>('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!kind) {
      setReason('');
      setNote('');
    }
  }, [kind]);

  const copy = COPY[kind ?? 'cancel'];

  return (
    <AlertDialog
      isOpen={!!kind}
      title={getTrans(copy.title)}
      onClose={onClose}
      onConfirm={() =>
        reason &&
        onConfirm({ reason, note: note.trim() ? note.trim() : undefined })
      }
      cancelLabel={getTrans('general.back')}
      confirmLabel={getTrans(copy.confirm)}
      confirmColor={kind === 'cancel' ? 'error' : 'primary'}
      isConfirmDisabled={!reason || isSubmitting}
      isCancelDisabled={isSubmitting}
    >
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
      >
        <Text variant="body2">{getTrans(copy.body)}</Text>
        <TextField
          select
          fullWidth
          label={getTrans('interviews.reason.label')}
          value={reason}
          onChange={event =>
            setReason(event.target.value as InterviewChangeReason)
          }
          SelectProps={{ inputProps: { 'data-testid': 'interview-reason' } }}
        >
          {REASONS[kind ?? 'cancel'].map(value => (
            <MenuItem key={value} value={value}>
              {getTrans(REASON_COPY[value])}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          multiline
          minRows={2}
          value={note}
          onChange={event =>
            setNote(event.target.value.slice(0, NOTE_MAX_LENGTH))
          }
          label={getTrans('interviews.reason.note')}
        />
      </DialogContent>
      {errorMessage && (
        <Alert severity="error" sx={{ mx: 3, mb: 1 }}>
          {errorMessage}
        </Alert>
      )}
    </AlertDialog>
  );
};
